import React, { useState, useRef, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, Link2, LayoutGrid } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
import { useSuppressFloatingCta } from './floatingCtaContext';
// Authentic full-color brand mark with a robust fallback chain so a tile is
// NEVER blank/broken:
//   1. full-color dashboard-icons (walkxcode)  ->
//   2. simpleicons                             ->
//   3. a brand-tinted monogram chip
type Brand = {
  // dashboard-icons slug (hyphenated where needed), e.g. "microsoft-teams"
  iconSlug: string;
  // simpleicons slug fallback, e.g. "microsoftteams"
  simpleSlug: string;
  name: string;
  // monogram fallback styling
  mono: string;
  brandColor: string;
};
const BRANDS: Record<string, Brand> = {
  slack: {
    iconSlug: 'slack',
    simpleSlug: 'slack',
    name: 'Slack',
    mono: 'S',
    brandColor: '#611f69'
  },
  teams: {
    iconSlug: 'microsoft-teams',
    simpleSlug: 'microsoftteams',
    name: 'Teams',
    mono: 'T',
    brandColor: '#4b53bc'
  },
  hubspot: {
    iconSlug: 'hubspot',
    simpleSlug: 'hubspot',
    name: 'HubSpot',
    mono: 'H',
    brandColor: '#ff7a59'
  },
  notion: {
    iconSlug: 'notion',
    simpleSlug: 'notion',
    name: 'Notion',
    mono: 'N',
    brandColor: '#2B211A'
  },
  salesforce: {
    iconSlug: 'salesforce',
    simpleSlug: 'salesforce',
    name: 'Salesforce',
    mono: 'S',
    brandColor: '#00A1E0'
  },
  linear: {
    iconSlug: 'linear',
    simpleSlug: 'linear',
    name: 'Linear',
    mono: 'L',
    brandColor: '#5E6AD2'
  }
};
function BrandLogo({
  brand,
  className = 'w-5 h-5'
}: {
  brand: keyof typeof BRANDS;
  className?: string;
}) {
  const b = BRANDS[brand];
  // 0 = dashboard-icons, 1 = simpleicons, 2 = monogram
  const [stage, setStage] = useState(0);
  if (stage >= 2) {
    return <span className={`flex items-center justify-center rounded-md text-white font-bold ${className}`} style={{
      backgroundColor: b.brandColor
    }} aria-hidden="true" data-id="element-1295">
        <span className="text-[10px] leading-none" data-id="element-1296">{b.mono}</span>
      </span>;
  }
  const src = stage === 0 ? `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${b.iconSlug}.svg` : `https://cdn.simpleicons.org/${b.simpleSlug}`;
  return <img key={stage} src={src} alt={b.name} className={`${className} object-contain`} loading="lazy" onError={() => setStage(s => s + 1)} data-id="element-1297" />;
}
export function GettingStartedSteps() {
  // While this section's own "Get started for free" CTA is on screen, suppress
  // the global floating "Try Giga for free" button so the two don't stack /
  // overlap on mobile.
  const ctaRef = useRef<HTMLDivElement>(null);
  const ctaInView = useInView(ctaRef, {
    margin: '0px 0px -10% 0px'
  });
  useSuppressFloatingCta(ctaInView);
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32 overflow-hidden" data-id="element-1298">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-1299">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20" data-id="element-1300">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-1301">
            Plug-and-play
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-1302">
            Up and running in 2 minutes
          </h2>
          <p className="text-base md:text-lg text-neutral-500 font-medium leading-relaxed max-w-2xl mx-auto" data-id="element-1303">
            No setup project. No IT ticket. No training videos.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16 md:mb-20" data-id="element-1304">
          {/* Card 1 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5
        }} className="group flex flex-col bg-white rounded-2xl md:rounded-[2rem] border border-neutral-200/70 shadow-[0_1px_3px_rgba(43,33,26,0.04)] overflow-hidden hover:shadow-[0_24px_60px_-12px_rgba(43,33,26,0.18)] transition-all duration-500 hover:-translate-y-1" data-id="element-1305">
            {/* Visual — instead of a noisy status row, this reads as the
             actual install moment: two clean, premium "connect" buttons. The
             Slack action is primary and live; Teams is a quiet, disabled
             secondary tagged "Soon". This is the single point of the step —
             one tap to add Giga — with nothing competing for attention. */}
            <div className="relative h-60 sm:h-64 bg-gradient-to-b from-[#2B211A] to-[#160F0A] px-6 sm:px-8 flex flex-col items-center justify-center gap-3 overflow-hidden" data-id="element-1306">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(234,88,12,0.18),transparent_55%)] opacity-60 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1307" />
              {/* Dotted halftone pattern, anchored to the TOP of the dark header
               and faded out toward the mockup so it reads as texture, not
               noise behind the content. */}
              <div aria-hidden="true" className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none [mask-image:linear-gradient(to_bottom,black,transparent_75%)]" data-id="element-1308" />
              <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/noise.png')] opacity-[0.05] mix-blend-overlay" data-id="element-1309" />
              <div className="absolute inset-x-0 top-0 h-px bg-white/10" data-id="element-1310" />
              <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)] pointer-events-none" data-id="element-1311" />

              {/* Primary — Add to Slack. A solid white OAuth-style button with
               the full-color Slack mark; the live, obvious action. */}
              <div className="relative z-10 w-full max-w-[240px] flex items-center justify-center gap-2.5 rounded-xl bg-white px-4 py-3 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)] transform group-hover:-translate-y-0.5 transition-transform duration-500" data-id="element-1312">
                <BrandLogo brand="slack" className="w-5 h-5 shrink-0" data-id="element-1313" />
                <span className="text-sm font-bold text-[#2B211A]" data-id="element-1314">
                  Add to Slack
                </span>
              </div>

              {/* Secondary — Continue with Teams. Quiet, outlined, and disabled
               with a small "Soon" tag since it isn't live yet. */}
              <div className="relative z-10 w-full max-w-[240px] flex items-center justify-center gap-2.5 rounded-xl border border-white/15 px-4 py-3 transform group-hover:translate-y-0.5 transition-transform duration-500" data-id="element-1315">
                <BrandLogo brand="teams" className="w-5 h-5 shrink-0 opacity-70" data-id="element-1316" />
                <span className="text-sm font-bold text-white/70" data-id="element-1317">
                  Continue with Teams
                </span>
                <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-white/45 bg-white/10 rounded-full px-1.5 py-0.5" data-id="element-1318">
                  Soon
                </span>
              </div>
            </div>
            {/* Text */}
            <div className="p-6 sm:p-8 md:p-10 flex-1 flex flex-col bg-white" data-id="element-1319">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#EA580C] mb-3" data-id="element-1320">
                Step 01
              </span>
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] tracking-tight leading-tight mb-4" data-id="element-1321">
                Add to Slack or Teams
              </h3>
              <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium" data-id="element-1322">
                Install Giga from the app directory. It shows up like any new
                teammate, ready to talk.
              </p>
            </div>
          </motion.div>

          {/* Card 2 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="group flex flex-col bg-white rounded-2xl md:rounded-[2rem] border border-neutral-200/70 shadow-[0_1px_3px_rgba(43,33,26,0.04)] overflow-hidden hover:shadow-[0_24px_60px_-12px_rgba(43,33,26,0.18)] transition-all duration-500 hover:-translate-y-1" data-id="element-1323">
            {/* Visual — deliberately simple: instead of a noisy orbit of brand
             logos, we show the single CONCEPT of linking. The Giga mark and a
             neutral "your tools" tile sit side by side, joined by one quiet
             link icon — "connect" reads instantly without the clutter. */}
            <div className="relative h-60 sm:h-64 bg-gradient-to-b from-[#2B211A] to-[#160F0A] flex items-center justify-center overflow-hidden" data-id="element-1324">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(234,88,12,0.18),transparent_70%)] opacity-60 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1325" />
              {/* Dotted halftone pattern, anchored to the TOP of the dark header
               and faded out toward the mockup so it reads as texture, not
               noise behind the content. */}
              <div aria-hidden="true" className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none [mask-image:linear-gradient(to_bottom,black,transparent_75%)]" data-id="element-1326" />
              <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/noise.png')] opacity-[0.05] mix-blend-overlay" data-id="element-1327" />
              <div className="absolute inset-x-0 top-0 h-px bg-white/10" data-id="element-1328" />
              <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)] pointer-events-none" data-id="element-1329" />

              <div className="relative z-10 flex items-center gap-4 sm:gap-5" data-id="element-1330">
                {/* Giga */}
                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-[#241A14] border border-white/10 shadow-[0_12px_30px_-8px_rgba(0,0,0,0.6)] transform group-hover:-translate-x-1 transition-transform duration-500" data-id="element-1331">
                  <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="Giga" className="w-8 h-8 object-contain [filter:brightness(0)_saturate(100%)_invert(48%)_sepia(78%)_saturate(2476%)_hue-rotate(346deg)_brightness(97%)_contrast(94%)]" data-id="element-1332" />
                </div>

                {/* The link itself — one quiet, vague icon on a dashed seam,
                 standing in for "connect" without naming any single tool. */}
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[#EA580C]/15 border border-[#EA580C]/30 text-[#FB923C] shadow-[0_0_24px_-6px_rgba(234,88,12,0.6)] transform group-hover:scale-110 transition-transform duration-500" data-id="element-1333">
                  <Link2 size={18} strokeWidth={2.25} data-id="element-1334" />
                </div>

                {/* Your tools — a neutral, unbranded tile representing whatever
                 the user already runs on. */}
                <div className="flex flex-col items-center justify-center gap-1.5 w-16 h-16 rounded-2xl bg-white/[0.06] border border-white/15 backdrop-blur-sm transform group-hover:translate-x-1 transition-transform duration-500" data-id="element-1335">
                  <LayoutGrid size={22} strokeWidth={1.75} className="text-white/80" data-id="element-1336" />
                </div>
              </div>

              {/* Caption — names the concept in words so the simple visual is
               unmistakable. */}
              <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[11px] font-semibold uppercase tracking-[0.25em] text-white/40" data-id="element-1337">
                Link your tools
              </span>
            </div>
            {/* Text */}
            <div className="p-6 sm:p-8 md:p-10 flex-1 flex flex-col bg-white" data-id="element-1338">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#EA580C] mb-3" data-id="element-1339">
                Step 02
              </span>
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] tracking-tight leading-tight mb-4" data-id="element-1340">
                Connect your tools
              </h3>
              <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium" data-id="element-1341">
                Link your CRM, Drive, calendar, and analytics. Each connection
                lets Giga act, not just read.
              </p>
            </div>
          </motion.div>

          {/* Card 3 */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="group flex flex-col bg-white rounded-2xl md:rounded-[2rem] border border-neutral-200/70 shadow-[0_1px_3px_rgba(43,33,26,0.04)] overflow-hidden hover:shadow-[0_24px_60px_-12px_rgba(43,33,26,0.18)] transition-all duration-500 hover:-translate-y-1" data-id="element-1342">
            {/* Visual — instead of a big white chat panel, this reads as the
             moment a user is ABOUT to assign a job: a glassy Slack-style
             composer sitting on the brown stage with a half-typed "@Giga …"
             message and a live caret, so the brown background carries the
             scene rather than a dominant white surface. */}
            <div className="relative h-60 sm:h-64 bg-gradient-to-b from-[#2B211A] to-[#160F0A] flex items-center justify-center px-5 sm:px-6 overflow-hidden" data-id="element-1343">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(234,88,12,0.18),transparent_60%)] opacity-60 group-hover:opacity-100 transition-opacity duration-500" data-id="element-1344" />
              {/* Dotted halftone pattern, anchored to the TOP of the dark header
               and faded out toward the mockup so it reads as texture, not
               noise behind the content. */}
              <div aria-hidden="true" className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none [mask-image:linear-gradient(to_bottom,black,transparent_75%)]" data-id="element-1345" />
              <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/noise.png')] opacity-[0.05] mix-blend-overlay" data-id="element-1346" />
              <div className="absolute inset-x-0 top-0 h-px bg-white/10" data-id="element-1347" />
              <div className="absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.4)] pointer-events-none" data-id="element-1348" />

              <div className="relative z-10 w-full max-w-[300px] flex flex-col gap-2.5" data-id="element-1349">
                {/* Channel context line — quietly grounds the composer in a
                 real workspace channel. */}
                <div className="flex items-center gap-1.5 text-white/40 text-[11px] font-semibold" data-id="element-1350">
                  <span className="text-white/30" data-id="element-1351">#</span>
                  operations
                </div>

                {/* Half-typed @Giga mention + blinking caret — no glass
                 composer, just the message sitting on the stage. */}
                <div className="relative transform group-hover:-translate-y-1 transition-transform duration-500" data-id="element-1352">
                  <p className="relative text-[15px] leading-relaxed text-white/90 font-medium" data-id="element-1353">
                    <span className="font-semibold text-[#FB923C]" data-id="element-1354">@Giga</span>{' '}
                    find us a cheaper packaging supplier
                    {/* Blinking caret */}
                    <motion.span aria-hidden="true" className="inline-block w-[2px] h-[1.05em] -mb-[0.15em] ml-0.5 bg-[#FB923C] align-middle" animate={{
                    opacity: [1, 1, 0, 0]
                  }} transition={{
                    duration: 1.1,
                    repeat: Infinity,
                    ease: 'linear'
                  }} data-id="element-1355" />
                  </p>

                  {/* Footer row — formatting affordances + a primed send
                   button, reinforcing "about to send". */}
                  <div className="relative mt-3 flex items-center justify-between" data-id="element-1356">
                    <div className="flex items-center gap-2 text-white/35" data-id="element-1357">
                      <span className="text-xs font-bold" data-id="element-1358">B</span>
                      <span className="text-xs italic font-semibold" data-id="element-1359">i</span>
                      <span className="text-xs font-mono" data-id="element-1360">{'</>'}</span>
                    </div>
                    <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-[#2EB67D] text-white shadow-[0_6px_16px_-4px_rgba(46,182,125,0.6)]" data-id="element-1361">
                      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 -ml-px" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-id="element-1362">
                        <path d="m22 2-7 20-4-9-9-4Z" data-id="element-1363" />
                        <path d="M22 2 11 13" data-id="element-1364" />
                      </svg>
                    </span>
                  </div>
                </div>

                {/* Tiny helper line, like Slack's hint under the composer. */}
                <div className="flex items-center gap-1.5 text-white/30 text-[10px] font-medium" data-id="element-1365">
                  <span className="text-[#FB923C]" data-id="element-1366">↵</span>
                  Giga picks it up the moment you hit send
                </div>
              </div>
            </div>
            {/* Text */}
            <div className="p-6 sm:p-8 md:p-10 flex-1 flex flex-col bg-white" data-id="element-1367">
              <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#EA580C] mb-3" data-id="element-1368">
                Step 03
              </span>
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] tracking-tight leading-tight mb-4" data-id="element-1369">
                Assign your first job
              </h3>
              <p className="text-neutral-500 leading-relaxed text-sm md:text-base font-medium" data-id="element-1370">
                Give Giga a task that eats your week. It does it, then runs it
                on a schedule if you want.
              </p>
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <div ref={ctaRef} className="flex justify-center" data-id="element-1371">
          <WaitlistCTA className="group inline-flex items-center justify-center gap-2.5 rounded-full bg-[#EA580C] px-8 py-4 text-base font-bold text-white shadow-lg shadow-[#EA580C]/20 transition-all duration-300 hover:bg-[#d24e09] hover:scale-105" data-id="element-1372">
            Get started for free
            <ArrowRight size={18} strokeWidth={2.5} className="transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-1373" />
          </WaitlistCTA>
        </div>
      </div>
    </section>;
}